/**
 * Auth Pinia Store
 * Replaces src/store/modules/auth.js
 */
import { defineStore } from 'pinia'
import { Preferences as Storage } from '@capacitor/preferences'
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  OAuthProvider,
  SAMLAuthProvider,
} from 'firebase/auth'
import { auth as firebaseAuth } from '@/shared/firebase-config'
import axios from 'axios'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '@/shared/env'
import { jwtDecrypt } from '@/shared/helper'
import { getQueryClient, clearQueryCache } from '@/shared/queryClient'
import { userDataQueryOptions } from '@/composables/useUserData'
import { socialConnectionsQueryOptions } from '@/composables/useSocialConnections'
import { roleDetailsQueryOptions } from '@/composables/useRoleDetails'
import { talkToUsLinkQueryOptions } from '@/composables/useTalkToUsLink'
import moment from 'moment-timezone'
import type { AuthState, AuthData, MFAState, MultipleTokenEntry, LoginStatus } from '@/shared/types'
import { useChatStore } from '@/stores/chat'

// ---------------------------------------------------------------------------
// Constants — centralised storage key registry prevents typos across the store
// ---------------------------------------------------------------------------

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  AUTHKEY: 'authkey',
  MULTIPLE_TOKENS: 'multipleTokens',
  USER: 'user',
  ENVIRONMENT: 'environment',
  CACHE_OWNER: 'jmpr_cache_owner',
} as const

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

/** Shape of the payload accepted by _setMFA */
interface SetMFAPayload {
  mfa: boolean
  activation: boolean
  qr?: string
  type?: string
  token?: { access?: string }
}

// ---------------------------------------------------------------------------
// Private helpers (not exported — module-local)
// ---------------------------------------------------------------------------

const emptyAuthData = (): AuthData => ({
  token: undefined,
  refreshToken: undefined,
  authkey: undefined,
  tokenExp: undefined,
  userId: undefined,
  userName: undefined,
  email: undefined,
  user_type: undefined,
  is_admin: false,
  group_id: undefined,
  permissions: null,
  platform_version: undefined,
  sso_login: false,
})

const buildAuthDataFromJwt = (access: string | undefined, refresh?: string, authkey?: string): AuthData => {
  if (!access) return emptyAuthData()
  const decoded = jwtDecrypt(access)
  if (!decoded) return emptyAuthData()
  return {
    token: access,
    refreshToken: refresh,
    authkey,
    tokenExp: decoded.exp,
    userId: decoded.user_id,
    userName: decoded.username,
    email: decoded.username,
    user_type: decoded.user_type,
    is_admin: decoded.is_admin,
    group_id: decoded.group_id,
    permissions: decoded.permissions,
    platform_version: decoded.platform_version,
    sso_login: decoded.sso_login === true,
  }
}

/** Sanitise a raw Capacitor Preferences value — treats 'undefined'/'null' strings as absent */
const sanitizeStorageValue = (r: { value: string | null }): string | undefined =>
  r.value && r.value !== 'undefined' && r.value !== 'null' ? r.value : undefined

const getTokenDataFromStorage = async () => {
  const [accessRes, refreshRes, multipleRes, authkeyRes] = await Promise.all([
    Storage.get({ key: STORAGE_KEYS.ACCESS_TOKEN }),
    Storage.get({ key: STORAGE_KEYS.REFRESH_TOKEN }),
    Storage.get({ key: STORAGE_KEYS.MULTIPLE_TOKENS }),
    Storage.get({ key: STORAGE_KEYS.AUTHKEY }),
  ])
  return {
    access: sanitizeStorageValue(accessRes),
    refresh: sanitizeStorageValue(refreshRes),
    authkey: sanitizeStorageValue(authkeyRes),
    multipleTokens: sanitizeStorageValue(multipleRes),
  }
}

const createProvider = (config: any) => {
  switch (config.type.toLowerCase()) {
    case 'oidc': {
      const oidcProvider = new OAuthProvider(config.providerId)
      if (config.tenant?.tenant_id) {
        oidcProvider.setCustomParameters({ ...config.customParameters, tenant: config.tenant.tenant_id })
      } else if (config.customParameters) {
        oidcProvider.setCustomParameters(config.customParameters)
      }
      if (config.scopes) config.scopes.forEach((s: string) => oidcProvider.addScope(s))
      return oidcProvider
    }
    case 'saml': {
      const samlProvider = new SAMLAuthProvider(config.providerId)
      if (config.tenant?.tenant_id) {
        samlProvider.setCustomParameters({ ...config.customParameters, tenant: config.tenant.tenant_id })
      } else if (config.customParameters) {
        samlProvider.setCustomParameters(config.customParameters)
      }
      return samlProvider
    }
    case 'password':
      throw new Error('Password sign-in is not supported with this method.')
    default:
      throw new Error(`Unsupported provider type: ${config.type}`)
  }
}

/**
 * Normalises the raw SSO config from the API (snake_case) into the shape
 * expected by createProvider (camelCase) and kicks off the Firebase popup flow.
 */
const signInWithDynamicProvider = async (config: any) => {
  if (!config?.type || !config?.provider_id) throw new Error('Invalid SSO configuration')
  const normalizedConfig = {
    type: config.type,
    providerId: config.provider_id,
    customParameters: config.custom_parameters,
    scopes: config.scopes,
    tenant: config.tenant_id,
    emailConfig: config.email_config,
  }
  firebaseAuth.tenantId = normalizedConfig.tenant ?? null
  const provider = createProvider(normalizedConfig)
  const userCredential = await signInWithPopup(firebaseAuth, provider)
  const idToken = await userCredential.user.getIdToken()
  if (!idToken) throw new Error('Failed to retrieve ID token from SSO provider.')
  return apiInterceptor.post(
    environment('VUE_APP_API_URL') + 'auth/sso/google/token-exchange',
    { id_token: idToken, access_token: idToken },
    { headers: { 'Content-Type': 'application/json' } }
  )
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    authData: emptyAuthData(),
    loginStatus: 'pending',
    multipleTokens: [],
    mfa: {
      enabled: false,
      activation: false,
      qr: undefined,
      type: undefined,
      temp_access_token: undefined,
      expiry: undefined,
    },
    userData: undefined,
    roleDetails: {},
    socialConnections: { whatsapp: [], googlercs: [], sms: [], email: [] },
  }),

  getters: {
    isLoggedIn: (state): boolean => state.loginStatus === 'success',
    isAdmin: (state): boolean => state.authData.is_admin,
    isOwner: (state): boolean => state.authData.user_type === 'owner',
    isPlatformLiteVersion: (state): boolean => state.authData.platform_version === 'v2',
    isLoggedInViaSSO: (state): boolean => state.authData.sso_login === true,
    getAuthData: (state): AuthData => state.authData,
    getLoginStatus: (state): LoginStatus => state.loginStatus,
    getMFA: (state): MFAState => state.mfa,
    getGroupId: (state): string | undefined => state.authData.group_id,
    getPermissions: (state): Record<string, boolean> | null => state.authData.permissions,
    getMultipleAccounts: (state): Pick<MultipleTokenEntry, 'name' | 'shop_logo' | 'shop_name' | 'shop_link'>[] =>
      state.multipleTokens.map(({ name, shop_logo, shop_name, shop_link }) => ({
        name,
        shop_logo,
        shop_name,
        shop_link,
      })),
    /**
     * Returns the user account data with the raw API envelope stripped.
     * Guards against stale persisted cache entries that still have the shape
     * `{ success: true, data: { ... } }` by unwrapping the `data` property.
     * Components should use this getter instead of accessing `userData` directly.
     */
    cleanUserData: (state): any => {
      const cached = state.userData
      if (cached && typeof cached === 'object' && 'data' in cached && 'success' in cached) {
        return (cached as any).data
      }
      return cached ?? undefined
    },
  },

  actions: {
    // ------------------------------------------------------------------
    // Internal state helpers (prefixed with _)
    // ------------------------------------------------------------------

    _saveAuthToken(payload: { access?: string; refresh?: string; authkey?: string } | undefined): void {
      this.authData = payload?.access
        ? buildAuthDataFromJwt(payload.access, payload.refresh, payload.authkey)
        : emptyAuthData()
    },

    _saveLoginStatus(status: LoginStatus): void {
      if (status === 'pending') {
        this.mfa = {
          enabled: false,
          activation: false,
          qr: undefined,
          type: undefined,
          temp_access_token: undefined,
          expiry: undefined,
        }
      }
      this.loginStatus = status
    },

    _setMFA(payload: SetMFAPayload): void {
      this.mfa.enabled = payload.mfa
      this.mfa.activation = payload.activation
      this.mfa.qr = payload.qr
      this.mfa.type = payload.type
      this.mfa.temp_access_token = payload.token?.access
      this.mfa.expiry = moment().add(5, 'minutes').toISOString()
    },

    _disableMFA(): void {
      this.mfa.enabled = false
    },

    // ------------------------------------------------------------------
    // Public actions
    // ------------------------------------------------------------------

    async clearAccountData(isLogout: boolean): Promise<void> {
      const keysToRemove: string[] = [
        STORAGE_KEYS.CACHE_OWNER,
        STORAGE_KEYS.ACCESS_TOKEN,
        STORAGE_KEYS.REFRESH_TOKEN,
        STORAGE_KEYS.AUTHKEY,
        STORAGE_KEYS.USER,
      ]
      if (isLogout) keysToRemove.push(STORAGE_KEYS.MULTIPLE_TOKENS)
      await clearQueryCache()
      await Promise.all(keysToRemove.map((key) => Storage.remove({ key })))
      this.userData = undefined
      this.roleDetails = {}
      this.socialConnections = { whatsapp: [], googlercs: [], sms: [], email: [] }
      useChatStore().disconnectRealtime()
    },

    /**
     * Re-fetches GET social/v1/connections and mirrors the result into
     * `socialConnections`. Call this after any connect / edit / disconnect so the
     * UI renders the fresh connected-accounts list.
     *
     * Always goes through `fetchQuery` with the canonical `socialConnectionsQueryOptions`
     * rather than `queryClient.refetchQueries(['social-connections'])`, for two reasons:
     *
     *   1. A query restored from the persisted cache (see queryClient.js) is rebuilt by
     *      TanStack's `hydrate()` without a `queryFn`. `refetchQueries` then calls
     *      `query.fetch()` on it, which rejects with `Missing queryFn` and is swallowed
     *      internally — no request is made and the pre-disconnect data stays in cache.
     *      It only worked when some other mounted component happened to be observing the
     *      query and could lend its `queryFn`, which is what made the bug intermittent.
     *   2. `staleTime: 0` overrides the query's `staleTime: Infinity`, so the round-trip
     *      is guaranteed instead of being served from cache.
     *
     * Failures are swallowed (logged only), matching `refreshChannel` in
     * useConnectionVerification and preserving the previous behaviour of
     * `refetchQueries`, which never rejected. The already-shown success toast for the
     * connect / disconnect itself stays accurate and the rest of the caller's handler
     * still runs; the list simply keeps the last known data until the next load.
     *
     * @returns The connections payload currently held in the store.
     */
    async refreshSocialConnections(): Promise<AuthState['socialConnections']> {
      try {
        const data = await getQueryClient().fetchQuery({
          ...socialConnectionsQueryOptions,
          staleTime: 0,
        })
        this.socialConnections = (data ?? { whatsapp: [], googlercs: [], sms: [], email: [] }) as any
      } catch (error) {
        console.error('Failed to refresh social connections', error)
      }
      return this.socialConnections
    },

    async checkToken(): Promise<boolean | undefined> {
      try {
        const qc = getQueryClient()
        const tokenData = await getTokenDataFromStorage()
        const currentUserId = tokenData.access ? String(jwtDecrypt(tokenData.access)?.user_id ?? '') : ''
        const currentEnv = (await Storage.get({ key: STORAGE_KEYS.ENVIRONMENT })).value || ''
        const cacheOwner = `${currentUserId}:${currentEnv}`
        const { value: storedCacheOwner } = await Storage.get({ key: STORAGE_KEYS.CACHE_OWNER })
        if (storedCacheOwner && storedCacheOwner !== cacheOwner) {
          await clearQueryCache()
        }
        // Pre-populate TanStack Query caches using the canonical queryOptions from each
        // composable — single source of truth for queryKey and queryFn.
        const [accountData, socialData, roleData] = await Promise.all([
          qc.ensureQueryData(userDataQueryOptions),
          qc.ensureQueryData(socialConnectionsQueryOptions),
          qc.ensureQueryData(roleDetailsQueryOptions),
          qc.ensureQueryData(talkToUsLinkQueryOptions).catch(() => undefined),
        ])
        if (!accountData) return
        // Mirror into Pinia state so Vue computed properties react to account switches.
        this.userData = accountData as any
        this.roleDetails = (roleData ?? {}) as any
        this.socialConnections = (socialData ?? { whatsapp: [], googlercs: [], sms: [] }) as any
        await Storage.set({ key: STORAGE_KEYS.CACHE_OWNER, value: cacheOwner })
        this._saveLoginStatus('success')
        const chatStore = useChatStore()
        chatStore.disconnectRealtime()
        chatStore.initRealtime({
          referallshopName: this.cleanUserData?.referallshopName ?? '',
          group_id: this.authData.group_id,
          authkey: this.authData.authkey,
          token: this.authData.token,
          refreshToken: this.authData.refreshToken,
        })
        return true
      } catch (error) {
        console.error('checkToken failed:', error)
      }
    },

    /**
     * Unified login entry-point. Dispatches to a focused private handler
     * based on which field of `payload` is present.
     */
    async loginUser(payload: Record<string, any>): Promise<any> {
      try {
        if (payload.mfaOTP) return await this._loginWithMfaOtp(payload.mfaOTP)
        if (payload.forceMFAType) return await this._loginWithForceMfa(payload.forceMFAType)
        if (payload.shop_name) return await this._loginWithAccount(payload.shop_name)
        if (payload.lookupDomain) return await this._loginWithSsoDomain(payload.lookupDomain)
        return await this._loginWithCredentials(payload)
      } catch (error) {
        console.error('loginUser failed:', error)
        this._saveAuthToken(undefined)
        return error
      }
    },

    async loginJupiterUser(payload: Record<string, any>): Promise<boolean> {
      try {
        await this._checkLoginResponse(payload)
        return true
      } catch (error) {
        console.error('loginJupiterUser failed:', error)
        return false
      }
    },

    async loadStorageTokens(): Promise<void> {
      const tokenData = await getTokenDataFromStorage()
      if (tokenData.access) {
        this._saveAuthToken(tokenData)
        if (tokenData.multipleTokens) {
          try {
            this.multipleTokens = JSON.parse(tokenData.multipleTokens)
          } catch {
            console.error('Failed to parse multipleTokens from storage; resetting to empty.')
            this.multipleTokens = []
            await Storage.remove({ key: STORAGE_KEYS.MULTIPLE_TOKENS })
          }
        }
        await this.checkToken()
      } else {
        this._saveAuthToken(undefined)
      }
    },

    async saveTokensToStorage(payload: { access: string; refresh?: string; authkey?: string }): Promise<void> {
      await Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: payload.access })
      if (payload.refresh) await Storage.set({ key: STORAGE_KEYS.REFRESH_TOKEN, value: payload.refresh })
      if (payload.authkey) await Storage.set({ key: STORAGE_KEYS.AUTHKEY, value: payload.authkey })
      this._saveAuthToken(payload)
    },

    async refreshAccessToken(payload: { access: string }): Promise<void> {
      const oldToken = this.authData.token
      await Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: payload.access })
      const tokenData = await getTokenDataFromStorage()
      this._saveAuthToken(tokenData)
      // Keep the active account entry in multipleTokens in sync with the new access token.
      if (oldToken && this.multipleTokens.length > 0) {
        const idx = this.multipleTokens.findIndex((e) => e.access === oldToken)
        if (idx !== -1) {
          this.multipleTokens[idx].access = payload.access
          await Storage.set({ key: STORAGE_KEYS.MULTIPLE_TOKENS, value: JSON.stringify(this.multipleTokens) })
        }
      }
    },

    async logoutUser(): Promise<void> {
      try {
        await apiInterceptor.post(environment('VUE_APP_API_URL') + 'auth/logout', {
          access: this.authData.token,
          refresh: this.authData.refreshToken,
        })
      } catch (error) {
        // Non-critical: best-effort server-side logout; proceed with local cleanup
        console.log('logoutUser API call failed:', error)
      } finally {
        await this.clearAccountData(true)
        this._saveAuthToken(undefined)
        try {
          await firebaseSignOut(firebaseAuth)
          firebaseAuth.tenantId = null
        } catch (firebaseError) {
          console.log('Firebase sign-out failed:', firebaseError)
        }
      }
    },

    async resendMfaOtp(): Promise<boolean> {
      try {
        await apiInterceptor.post(environment('VUE_APP_API_URL') + 'auth/mfa-setup/resend', null, {
          headers: { Authorization: `Bearer ${this.mfa.temp_access_token}` },
        })
        this.mfa.expiry = moment().add(5, 'minutes').toISOString()
        return true
      } catch (error) {
        console.error('resendMfaOtp failed:', error)
        this._saveLoginStatus('pending')
        return false
      }
    },

    // ------------------------------------------------------------------
    // Private login flow handlers
    // ------------------------------------------------------------------

    async _loginWithMfaOtp(mfaOTP: string): Promise<any> {
      const response = await apiInterceptor
        .post(
          environment('VUE_APP_API_URL') + 'auth/token/mfa/verify',
          { code: mfaOTP },
          { headers: { Authorization: `Bearer ${this.mfa.temp_access_token}` } }
        )
        .catch((err: any) => {
          if (err?.response?.data?.code === 'token_not_valid') this._saveLoginStatus('pending')
          else if (err?.response) throw err.response
          throw err
        })
      if (response) await this._checkLoginResponse(response)
      return response
    },

    async _loginWithForceMfa(forceMFAType: string): Promise<any> {
      const response: any = await apiInterceptor.post(
        environment('VUE_APP_API_URL') + 'auth/mfa-setup',
        { type: forceMFAType },
        { headers: { Authorization: `Bearer ${this.mfa.temp_access_token}` } }
      )
      this._setMFA({
        mfa: true,
        activation: false,
        qr: response?.qr_code,
        type: response?.type,
        token: { access: this.mfa.temp_access_token ?? undefined },
      })
      this._saveLoginStatus('mfa')
      return response
    },

    async _loginWithAccount(shopName: string): Promise<MultipleTokenEntry> {
      const account = this.multipleTokens.find((e) => e.shop_name === shopName)!
      await Promise.all([
        Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: account.access }),
        Storage.set({ key: STORAGE_KEYS.REFRESH_TOKEN, value: account.refresh }),
        Storage.set({ key: STORAGE_KEYS.AUTHKEY, value: account.authkey }),
      ])
      this._saveAuthToken(account)
      await apiInterceptor.post(environment('VUE_APP_API_URL') + 'auth/auth-log/login-success')
      await this.checkToken()
      return account
    },

    async _loginWithSsoDomain(domain: string): Promise<any> {
      const response: any = await apiInterceptor.get(
        environment('VUE_APP_API_URL') + `auth/sso/providers/domain/${domain}`,
        { headers: { 'Content-Type': 'application/json' } }
      )
      const config = response[0]
      if (!config?.type || !config?.provider_id) {
        throw new Error(
          'This service is not available for your organisation. Try login with email + password instead.'
        )
      }
      if (config.type === 'password') return
      const exchangeResponse = await signInWithDynamicProvider(config)
      if (exchangeResponse) await this._checkLoginResponse(exchangeResponse)
      return exchangeResponse
    },

    async _loginWithCredentials(payload: Record<string, any>): Promise<any> {
      const response = await apiInterceptor.post(environment('VUE_APP_API_URL') + 'auth/token/mfa', payload)
      if (response) await this._checkLoginResponse(response)
      return response
    },

    // ------------------------------------------------------------------
    // Private response handler
    // ------------------------------------------------------------------

    // ------------------------------------------------------------------
    // Token refresh — migrated from global mixin
    // ------------------------------------------------------------------

    /**
     * Refreshes the access token using the stored refresh token.
     * Clears storage and returns undefined if no refresh token is present
     * or the refresh request fails.
     *
     * Uses a raw axios instance (no Authorization header) to avoid:
     *   1. Sending the expired Bearer token, which some servers reject with 401.
     *   2. Running through apiInterceptor's response interceptor, which unwraps
     *      data envelopes and throws for non-success — swallowing the new tokens.
     * Handles both enveloped ({ success: true, data: { access } }) and
     * unwrapped ({ access }) response shapes, mirroring apiInterceptor.js.
     */
    async refreshTokens(): Promise<{ access: string } | undefined> {
      if (!this.authData?.refreshToken) {
        console.info('No refresh token present', this.authData)
        await Storage.clear()
        return undefined
      }
      try {
        const rawAxios = axios.create()
        const rawResponse = await rawAxios.post(
          environment('VUE_APP_API_URL') + 'auth/token/refresh/',
          { refresh: this.authData.refreshToken },
          { headers: { 'x-device': 'web' } }
          // Intentionally no Authorization header — the refresh endpoint only
          // needs the refresh token in the body.
        )
        const body = rawResponse.data ?? {}
        const newAccess: string | null = body?.data?.access ?? body?.access ?? null
        if (newAccess) {
          await this.refreshAccessToken({ access: newAccess })
          return { access: newAccess }
        }
        console.error('Error getting new token from refresh token', body)
        await Storage.clear()
        return undefined
      } catch (error) {
        console.error('Error refreshing token', error)
        await Storage.clear()
        return undefined
      }
    },

    /**
     * Checks whether the current access token has expired and refreshes it
     * proactively if so. Call this before making API requests in long-lived
     * page sessions (e.g. analytics pages).
     *
     * Returns the refresh promise so callers can `await` the completed token
     * update before reading authData (e.g. building an iframe URL).
     */
    async checkAndRefreshToken(): Promise<{ access: string } | undefined> {
      if (Date.now() >= (this.authData.tokenExp ?? 0) * 1000) {
        return await this.refreshTokens()
      }
    },

    async _checkLoginResponse(response: any): Promise<void> {
      if (response?.accessToken?.length) {
        await Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: response.accessToken })
        if (response.refreshToken?.length) {
          await Storage.set({ key: STORAGE_KEYS.REFRESH_TOKEN, value: response.refreshToken })
        }
        this._saveAuthToken({ access: response.accessToken, refresh: response.refreshToken })
        await this.checkToken()
      } else if (response?.mfa === true) {
        this._setMFA(response)
        this._saveLoginStatus('mfa')
      } else if (response?.mfa === false) {
        this._setMFA(response)
        this._saveLoginStatus('force-mfa')
      } else if (response?.tokens?.length > 0) {
        this._disableMFA()
        if (response.tokens.length === 1) {
          const token = response.tokens[0]
          await Promise.all([
            Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: token.access }),
            Storage.set({ key: STORAGE_KEYS.REFRESH_TOKEN, value: token.refresh }),
            Storage.set({ key: STORAGE_KEYS.AUTHKEY, value: token.authkey }),
          ])
          this._saveAuthToken(token)
          await this.checkToken()
        } else {
          await Storage.set({ key: STORAGE_KEYS.MULTIPLE_TOKENS, value: JSON.stringify(response.tokens) })
          this.multipleTokens = response.tokens
          this._saveLoginStatus('halfway')
        }
      } else if (response?.data?.access && response?.data?.refresh) {
        this._disableMFA()
        await Promise.all([
          Storage.set({ key: STORAGE_KEYS.ACCESS_TOKEN, value: response.data.access }),
          Storage.set({ key: STORAGE_KEYS.REFRESH_TOKEN, value: response.data.refresh }),
          Storage.set({ key: STORAGE_KEYS.AUTHKEY, value: response.data.authkey }),
        ])
        this._saveAuthToken(response.data)
        await this.checkToken()
      }
    },
  },
})
